import { AvailabilityMeetingPlace } from '@/core/graphql/types'
import { t } from '@lingui/core/macro'
import { loadStateFromStorage } from '../lib/bookingStorage'
import { createCustomizedStepOrder, determineNextStep, determinePreviousStep, isStepCompleted } from '../lib/bookingUtils'
import { BookingState, BookingAction, BookingStepEnum, ConsultationMode } from './bookingTypes'

// Initial state
const initialState: BookingState = {
  isLoggedIn: false,
  meetingLinkSlug: '',
  currentStep: BookingStepEnum.SPECIALITIES,
  consultationMode: null,
  availabilityId: null,
  selectedSpeciality: null,
  meetingPlace: null,
  meetingPlaceId: null,
  meetingPlaceDescription: null,
  meetingPlaceDetails: null,
  startDate: null,
  formSubmissionId: null,
  formData: {},
  hasFormTemplate: false,
  patientInfo: {
    firstName: '',
    lastName: '',
    email: '',
  },
  expertiseData: {
    comment: '',
    files: [],
  },
  patientLocation: null,
  consents: null,
  companion: undefined,
  orderId: null,
  orderReferenceId: null,
  consultationId: null,
  requiresPayment: false,
  paymentCompleted: false,
  errors: {},
}

export function getInitialState(meetingLinkSlug: string, isLoggedIn: boolean, isImmediate: boolean = false) {
  const persistedState = loadStateFromStorage(initialState)

  // If we have a persisted state but it's for a DIFFERENT meeting link, starting fresh
  const shouldReset = persistedState && persistedState.meetingLinkSlug && persistedState.meetingLinkSlug !== meetingLinkSlug

  let currentState =
    persistedState && !shouldReset
      ? {
          ...persistedState,
          isLoggedIn,
          meetingLinkSlug,
        }
      : {
          ...initialState,
          isLoggedIn,
          meetingLinkSlug,
        }

  // If immediate mode requested via URL, apply immediate mode logic
  if (isImmediate) {
    currentState = {
      ...currentState,
      consultationMode: ConsultationMode.Immediate,
      meetingPlace: AvailabilityMeetingPlace.VisioConference,
      meetingPlaceId: null,
      meetingPlaceDescription: t`Video visit`,
    }
  }

  let currentStep = currentState.currentStep
  const steps = createCustomizedStepOrder(currentState)
  for (const step of steps) {
    if (isStepCompleted(step, currentState)) continue
    currentStep = step
    break
  }

  return {
    ...currentState,
    currentStep,
  }
}

// Main reducer function
export function bookingReducer(state: BookingState, action: BookingAction): BookingState {
  let newState = state
  switch (action.type) {
    case 'SET_MEETING_LINK_SLUG':
      newState = { ...state, meetingLinkSlug: action.payload }
      break
    case 'SET_IS_LOGGED_IN':
      newState = { ...state, isLoggedIn: action.payload }
      break
    case 'SET_STEP':
      newState = { ...state, currentStep: action.payload }
      break
    case 'SET_CONSULTATION_MODE': {
      const mode = action.payload
      const isImmediateMode = mode === ConsultationMode.Immediate
      newState = {
        ...state,
        consultationMode: mode,
        ...(isImmediateMode
          ? {
              meetingPlace: AvailabilityMeetingPlace.VisioConference,
              meetingPlaceId: null,
              meetingPlaceDescription: t`Video visit`,
              // Ensure patientLocation is not auto-declined so user sees the step
              patientLocation: state.patientLocation,
            }
          : {
              // Reset meeting place when switching to scheduled so user must choose
              meetingPlace: null,
              meetingPlaceId: null,
              meetingPlaceDescription: null,
              startDate: null, // Also reset start date
            }),
      }
      break
    }
    case 'SET_AVAILABILITY':
      newState = {
        ...state,
        availabilityId: action.payload.availabilityId,
        ...(action.payload.speciality ? { selectedSpeciality: action.payload.speciality } : {}),
      }
      break
    case 'SET_MEETING_PLACE': {
      const { place, placeId, description } = action.payload
      newState = {
        ...state,
        meetingPlace: place,
        meetingPlaceId: placeId,
        meetingPlaceDescription: description,
        startDate: place !== AvailabilityMeetingPlace.SecondOpinion ? state.startDate : null,
      }
      break
    }
    case 'SET_MEETING_PLACE_DETAILS':
      newState = { ...state, meetingPlaceDetails: action.payload }
      break
    case 'SET_START_DATE':
      newState = { ...state, startDate: action.payload }
      break
    case 'SET_FORM_SUBMISSION':
      newState = {
        ...state,
        formSubmissionId: action.payload.id,
        formData: { ...state.formData, ...action.payload.data },
      }
      break
    case 'UPDATE_FORM_DATA':
      newState = { ...state, formData: { ...state.formData, ...action.payload } }
      break
    case 'SET_PATIENT_INFO':
      newState = { ...state, patientInfo: { ...state.patientInfo, ...action.payload } }
      break
    case 'UPDATE_EXPERTISE_DATA':
      newState = { ...state, expertiseData: { ...state.expertiseData, ...action.payload } }
      break
    case 'SET_CONSENTS':
      newState = { ...state, consents: action.payload }
      break
    case 'SET_PATIENT_LOCATION':
      newState = { ...state, patientLocation: action.payload }
      break
    case 'SET_COMPANION':
      newState = { ...state, companion: action.payload }
      break
    case 'SET_ORDER_ID':
      newState = { ...state, orderId: action.payload }
      break
    case 'SET_ORDER_REFERENCE_ID':
      newState = { ...state, orderReferenceId: action.payload }
      break
    case 'SET_CONSULTATION_ID':
      newState = { ...state, consultationId: action.payload }
      break
    case 'SET_REQUIRES_PAYMENT':
      newState = { ...state, requiresPayment: action.payload }
      break
    case 'SET_PAYMENT_COMPLETED':
      newState = { ...state, paymentCompleted: action.payload }
      break
    case 'SET_HAS_FORM_TEMPLATE':
      newState = { ...state, hasFormTemplate: action.payload }
      break
    case 'SET_ERROR':
      newState = {
        ...state,
        errors: { ...state.errors, [action.payload.field]: action.payload.message },
      }
      break
    case 'CLEAR_ERROR': {
      const newErrors = { ...state.errors }
      delete newErrors[action.payload]
      newState = { ...state, errors: newErrors }
      break
    }
    case 'CLEAR_ALL_ERRORS':
      newState = { ...state, errors: {} }
      break
    case 'LOAD_STATE_FROM_STORAGE':
      newState = loadStateFromStorage(state) ?? state
      break
    case 'RESTORE_STATE':
      // Restore the entire state from the payload
      newState = action.payload
      break
    case 'RESET_STATE':
      sessionStorage.setItem('bookingState', JSON.stringify(initialState))
      newState = initialState
      break
    case 'SET_CURRENT_STEP':
      newState = { ...state, currentStep: action.payload }
      break
    default:
      break
  }
  if (action.goToNextStep) {
    const nextStep = determineNextStep(newState)
    if (nextStep) {
      newState = { ...newState, currentStep: nextStep }
    }
  } else if (action.goToPrevStep) {
    const prevStep = determinePreviousStep(newState)
    if (prevStep) {
      newState = { ...newState, currentStep: prevStep }
    }
  }
  return newState
}
